import { Outlet, useLocation, useMatches } from 'react-router';

import { ALERT_BANNER_DESKTOP_HEIGHT_IN_PX, ALERT_BANNER_MOBILE_HEIGHT_IN_PX, AlertBanner } from '@/components/shared/AlertBanner/AlertBanner';
import { computeAlertToDisplay } from '@/components/shared/AlertBanner/utils/computeAlertToDisplay';
import { SystemBanner } from '@/components/shared/SystemBanner/SystemBanner';
import { SYSTEM_BANNER_HEIGHT, withSystemBannerOffset } from '@/components/shared/SystemBanner/utils/systemBannerHeight';
import { EnableNotificationsBoundary } from '@/components/shared/EnableNotificationsBoundary/EnableNotificationsBoundary';
import { useInvalidateNotifications } from '@/features/notifications/hooks/useInvalidateNotifications';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageViewTracker } from '@/hooks/usePageViewTracker';
import { useRouteChangeAccessibility } from '@/hooks/useRouteChangeAccessibility';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import { APP_HEADER_HEIGHT_IN_PX, AppHeader } from '../../shared/AppHeader/AppHeader';
import { APP_NAVBAR_HEIGHT_IN_PX, AppNavbar } from '../../shared/AppNavbar/AppNavbar';
import { APP_SIDEBAR_WIDTH_IN_PX, AppSidebar } from '../../shared/AppSidebar/AppSidebar';
import { paths } from '@/config/paths.ts';

// To pass isSubpage to the AppLayout component, you can use the handle property in the route configuration.
// For example:
// {
//   path: '/subpage',
//   element: <Subpage />,
//   handle: { isSubpage: true },
// }

export const APP_LAYOUT_CONTENT_MAX_WIDTH_IN_PX = 1400;

export const MAIN_CONTENT_ID = 'main-content';
export const MOBILE_NAV_ID = 'mobile-nav';

interface AppLayoutHandle {
  isSubpage?: boolean;
}

export const AppLayout = () => {
  const { t } = useTranslation('components', { keyPrefix: 'appLayout' });
  const location = useLocation();

  useRouteChangeAccessibility();
  usePageViewTracker();
  useInvalidateNotifications();

  const { userData } = useAuthStore();

  const matches = useMatches();

  const currentHandle = [...matches].reverse().find((match): match is typeof match & { handle: AppLayoutHandle } => Boolean(match.handle))?.handle;

  const { isSubpage } = currentHandle ?? {};

  const isDesktop = useMediaQuery('desktop');

  const shouldDisplayDesktopSidebar = isDesktop;
  const shouldDisplayMobileElements = !isDesktop && !isSubpage;

  const alertToDisplay = computeAlertToDisplay({
    creditCardExpiriesAt: userData?.creditCardExpiriesAt,
    failedPayment: userData?.failedPayment ?? false,
    overdueInvoice: userData?.overdueInvoice ?? false,
  });

  const calculateContentMarginTop = () => {
    if (isDesktop) {
      return alertToDisplay ? withSystemBannerOffset(ALERT_BANNER_DESKTOP_HEIGHT_IN_PX) : SYSTEM_BANNER_HEIGHT;
    }

    const isNotificationsSubpage = location.pathname === paths.notifications.home.path;
    const mobileMarginTop = isNotificationsSubpage
      ? withSystemBannerOffset(ALERT_BANNER_MOBILE_HEIGHT_IN_PX + APP_HEADER_HEIGHT_IN_PX + 38)
      : withSystemBannerOffset(ALERT_BANNER_MOBILE_HEIGHT_IN_PX + APP_HEADER_HEIGHT_IN_PX);

    return alertToDisplay ? mobileMarginTop : withSystemBannerOffset(APP_HEADER_HEIGHT_IN_PX);
  };

  const contentStyle = {
    maxWidth: `${APP_LAYOUT_CONTENT_MAX_WIDTH_IN_PX}px`,
    marginTop: calculateContentMarginTop(),
    marginLeft: isDesktop ? `${APP_SIDEBAR_WIDTH_IN_PX}px` : 'initial',
  };

  return (
    <>
      <EnableNotificationsBoundary />
      <SystemBanner />
      <div id='start'>
        {!isDesktop && !isSubpage && (
          <a href={`#${MOBILE_NAV_ID}`} className='absolute top-0 left-0 z-100 -translate-y-full bg-white p-2 focus:translate-y-0'>
            {t('skipToMobileNav.label')}
          </a>
        )}
        <a href={`#${MAIN_CONTENT_ID}`} className='absolute top-0 left-0 z-100 -translate-y-full bg-white p-2 focus:translate-y-0'>
          {t('skipToContent.label')}
        </a>
        <div className='flex min-h-screen flex-col'>
          {shouldDisplayMobileElements && <AppHeader />}
          <div
            className='flex flex-1 justify-center'
            style={{
              paddingBottom: shouldDisplayMobileElements ? `${APP_NAVBAR_HEIGHT_IN_PX}px` : 'initial',
            }}
          >
            {shouldDisplayDesktopSidebar && (
              <AppSidebar style={{ paddingTop: withSystemBannerOffset(alertToDisplay ? ALERT_BANNER_DESKTOP_HEIGHT_IN_PX + 32 : 32) }} />
            )}
            <main id={MAIN_CONTENT_ID} className='desktop:px-10 flex flex-1 flex-col overflow-x-hidden px-5 pt-6 pb-20' style={contentStyle}>
              <AlertBanner alert={alertToDisplay} />
              <Outlet />
            </main>
          </div>
          {shouldDisplayMobileElements && <AppNavbar />}
        </div>
      </div>
    </>
  );
};
